/**
 * A partner unit's profile card — photo, country, giving link, badges, rollups.
 *
 * The sidebar of the partner unit record, and the same card wherever else a page has a
 * unit open: the updates report shows it beside the updates it is listing. It used to
 * have a near-copy there, close enough to look like the same card and different enough
 * to be noticed — the report labelled two of the rollups differently, linked neither the
 * country nor the record, and left the Donate button off a unit that has one, so a
 * reader who opened the report from the directory saw the unit they had just been
 * looking at described in other words. One card, one description.
 *
 * `identify` adds the code and name at the top, for a page whose own title is about
 * something else. On the record itself the header already says whose page it is, so
 * there the card opens straight into the photo.
 */
import { Link } from 'react-router-dom'
import { MediaImage } from './ImageUpload'
import { Card, Badge, ActivityBadge, CountryLabel, fmtInt, fmtDate, headcount, relDays } from './ui'
import { fundTypeLabel } from '../../shared/fundTypes.js'
import { relabelText, usePartnerTerm, useCode } from '../lib/terms'
import { Icon } from './Icon'

export function PartnerProfileCard({ partner: p, photo, identify = false }) {
  const code = useCode()
  const term = usePartnerTerm()
  const to = `/partners/${p.partner_id}`
  const openTitle = relabelText(`Open the partner unit record for ${code(p.s4ca_code)}`, term)
  const people = headcount(p)
  return (
    <div className="col partner-card">
      <Card>
        {identify ? (
          <div className="col" style={{ gap: 4 }}>
            {/* Both the code and the name lead to the record, the way they do on the
                directory's own cards. Partner records are in every view, view-only
                included, so the link needs no guard — see PartnerLabel in ui.jsx. */}
            <Link className="mono subtle small" to={to} title={openTitle}>
              {code(p.s4ca_code)}
            </Link>
            {/* Placeholder records for codes missing from the registry are named after
                the code itself, and a card that said it twice would read as a mistake.
                The report's strip of tags drops the repeat the same way. */}
            {p.display_name && p.display_name !== p.s4ca_code ? (
              <h2>
                <Link to={to} title={openTitle}>
                  {p.display_name}
                </Link>
              </h2>
            ) : null}
          </div>
        ) : null}

        {photo ? (
          <MediaImage id={photo.id} alt={photo.alt_text || p.display_name} className="partner-photo" />
        ) : (
          <div className="partner-photo-empty" title="No photo on file" aria-label="No photo on file">
            <Icon name="person" size={64} />
          </div>
        )}

        <div className="country-head">
          <span className="country-head-flag" aria-hidden="true">
            {p.flag_emoji || <Icon name="globe" size={30} />}
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="country-head-label">Country</div>
            <div className="country-head-name truncate">
              <CountryLabel id={p.country_id} name={p.country_name || 'Not recorded'} />
            </div>
          </div>
        </div>

        {/* The giving page for this unit, from the registry export's link column.
            An ordinary link wearing a button: it opens in the reader's own browser,
            and `noopener` keeps the page it opens from reaching back into this one.
            A unit with no link shows nothing rather than a button that goes nowhere. */}
        {p.donate_url ? (
          <>
            <hr />
            <a
              className="btn btn-donate btn-donate-lg"
              href={p.donate_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Donate
            </a>
            <div className="tiny subtle truncate donate-host" title={p.donate_url}>
              {p.donate_url.replace(/^https?:\/\//, '').replace(/^www\./, '')}
            </div>
          </>
        ) : null}

        <hr />

        <div className="row row-tight">
          <ActivityBadge activity={p.activity} />
          {/* What kind of record this is, for the two kinds that are not the default. The
              card is reached from three Register pages and from the Updates report, so it
              cannot rely on the reader knowing which page they came from. A staff unit
              wears nothing: it is what "a record in this registry" has always meant. */}
          {p.fund_type && p.fund_type !== 'staff' ? (
            <Badge tone="accent">{fundTypeLabel(p.fund_type)}</Badge>
          ) : null}
          {p.is_orphan ? (
            <Badge
              tone="warn"
              title={relabelText('This code appears in updates but not in the partner unit registry', term)}
            >
              not in registry
            </Badge>
          ) : null}
          {p.status !== 'active' ? <Badge>{p.status}</Badge> : null}
        </div>

        <hr />

        <dl className="kv">
          {/* First in the list, because it is a fact about the unit and everything under
              it is a reading of the unit's updates. Both numbers are shown when the unit
              is more than one person — a married couple serving together is the ordinary
              case — and the single total stands alone when it is not, rather than
              spelling out "1 staff, 0 non-staff" on three hundred records. */}
          <dt title={relabelText(people.title, term)}>{relabelText('Partners', term)}</dt>
          <dd>{people.label}</dd>
          {people.total > 1 ? (
            <>
              {/* Not relabelled: these two name the registry file's own columns, Staff
                  and non-Staff Partners, rather than what this installation calls a
                  person. The total above is the one that takes the installation's word. */}
              <dt>Staff</dt>
              <dd>{fmtInt(p.staff_partners ?? 0)}</dd>
              <dt>Non-staff</dt>
              <dd>{fmtInt(p.non_staff_partners ?? 0)}</dd>
            </>
          ) : null}
          <dt>Updates</dt>
          <dd>{fmtInt(p.newsletter_count)}</dd>
          <dt>First</dt>
          <dd>{fmtDate(p.first_newsletter)}</dd>
          <dt>Latest</dt>
          <dd title={p.last_newsletter ? relDays(p.last_newsletter) : undefined}>
            {fmtDate(p.last_newsletter)}
          </dd>
          <dt>Prayer Requests</dt>
          <dd>{fmtInt(p.prayer_request_count)}</dd>
          <dt>Highlighted</dt>
          <dd>{fmtInt(p.highlighted_count)}</dd>
          <dt>Avg. words</dt>
          <dd>{fmtInt(p.avg_word_count)}</dd>
          {p.initial_year ? (
            <>
              <dt>Since</dt>
              <dd>{p.initial_year}</dd>
            </>
          ) : null}
        </dl>
      </Card>
    </div>
  )
}
